import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  CreateTranscriptRequestDocument,
  TranscriptRecipientType,
} from '@sandtech-hub-monorepo/ehub-client';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@sandtech-hub-monorepo/ui-kit';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';

export interface RequestPrefill {
  recipientType: TranscriptRecipientType;
  institutionName: string;
  email: string;
  note: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewer: {
    firstName?: string | null;
    lastName?: string | null;
    primaryEmail: string;
  };
  /** Prefilled values when resubmitting a rejected request. */
  prefill?: RequestPrefill | null;
}

const RECIPIENT_TYPE_OPTIONS: {
  value: TranscriptRecipientType;
  label: string;
}[] = [
  {
    value: TranscriptRecipientType.UniversityCollege,
    label: 'University / College',
  },
  {
    value: TranscriptRecipientType.EmployerOrganization,
    label: 'Employer / Organization',
  },
  { value: TranscriptRecipientType.Personal, label: 'Personal (To myself)' },
  { value: TranscriptRecipientType.Other, label: 'Other' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RequestOfficialTranscriptModal = ({
  open,
  onOpenChange,
  viewer,
  prefill,
}: Props) => {
  const [recipientType, setRecipientType] = useState<TranscriptRecipientType>(
    TranscriptRecipientType.UniversityCollege
  );
  const [institutionName, setInstitutionName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  // Release authorization. Never prefilled: consent must be given explicitly
  // on every request, including resubmissions of a rejected one.
  const [authorized, setAuthorized] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [createRequest, { loading, error, reset }] = useMutation(
    CreateTranscriptRequestDocument,
    {
      refetchQueries: ['GetViewerTranscriptRequests'],
      onCompleted: () => setSubmitted(true),
    }
  );

  // On every open: clear a stale success screen or error (e.g. when the
  // previous submit settled after the modal was closed) and apply the
  // resubmit prefill.
  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setAuthorized(false);
    reset();
    if (prefill) {
      setRecipientType(prefill.recipientType);
      setInstitutionName(prefill.institutionName);
      setEmail(prefill.email);
      setNote(prefill.note);
    }
  }, [open, prefill, reset]);

  const isValid =
    institutionName.trim().length > 0 &&
    EMAIL_REGEX.test(email.trim()) &&
    authorized;

  const handleClose = () => {
    // Match the disabled Cancel button: the modal cannot be dismissed while
    // the request is in flight.
    if (loading) return;
    onOpenChange(false);
    setRecipientType(TranscriptRecipientType.UniversityCollege);
    setInstitutionName('');
    setEmail('');
    setNote('');
    setAuthorized(false);
    setSubmitted(false);
  };

  const viewerFullName =
    [viewer.firstName, viewer.lastName].filter(Boolean).join(' ') ||
    'Personal copy';

  const handleTypeChange = (value: string) => {
    const type = value as TranscriptRecipientType;
    const previousType = recipientType;
    setRecipientType(type);
    // "Personal (To myself)": the student is the recipient, so prefill their
    // own details (still editable).
    if (type === TranscriptRecipientType.Personal) {
      setEmail(viewer.primaryEmail);
      setInstitutionName(viewerFullName);
    } else if (previousType === TranscriptRecipientType.Personal) {
      // Leaving Personal: clear the auto-filled self details (but keep any
      // values the student typed over them).
      if (email === viewer.primaryEmail) setEmail('');
      if (institutionName === viewerFullName) setInstitutionName('');
    }
  };

  return (
    <Modal open={open} onOpenChange={handleClose}>
      <ModalContent className="max-w-md">
        {submitted ? (
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <div className="p-4 bg-primary/10 rounded-full text-primary">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-lg font-semibold">Request submitted</h2>
            <p className="text-sm text-muted-foreground">
              Your request has been sent to the Registrar for review. We will
              email you as soon as there is an update.
            </p>
            <ModalFooter className="pt-4 w-full">
              <Button className="w-full" onClick={handleClose}>
                Done
              </Button>
            </ModalFooter>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <h2 className="text-lg font-semibold">
                Request official transcript
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Please provide recipient details below.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label>Recipient type</Label>
                <Select value={recipientType} onValueChange={handleTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECIPIENT_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="institutionName">
                  Institution or organization name
                </Label>
                <Input
                  id="institutionName"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  placeholder="eg. University of Michigan"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="recipientEmail">Recipient email</Label>
                <Input
                  id="recipientEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="eg. admissions@example.edu"
                />
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  The transcript is delivered securely to this address.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="registrarNote">
                  Note to registrar (optional)
                </Label>
                <Textarea
                  id="registrarNote"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Any special instructions…"
                />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="releaseAuthorization"
                  checked={authorized}
                  onCheckedChange={(checked) => setAuthorized(checked === true)}
                  className="mt-0.5"
                />
                <label
                  htmlFor="releaseAuthorization"
                  className="text-sm text-foreground leading-normal cursor-pointer"
                >
                  I authorize Frontier to release my transcript as a shareable
                  link that anyone holding it can open for 30 days, and I
                  understand it can be viewed by anyone the recipient shares it
                  with.
                </label>
              </div>
              {error && (
                <p className="text-sm text-destructive">{error.message}</p>
              )}
            </div>
            <ModalFooter className="pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                className="gap-2"
                disabled={!isValid || loading}
                onClick={() =>
                  createRequest({
                    variables: {
                      input: {
                        recipientType,
                        recipientInstitutionName: institutionName.trim(),
                        recipientEmail: email.trim(),
                        note: note.trim() || undefined,
                      },
                    },
                  })
                }
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Submit for review
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default RequestOfficialTranscriptModal;
